import React from 'react';
import {
  Grid,
  TextField,
  InputAdornment,
  Button,
  Typography,
  Tooltip,
  CircularProgress,
  Box,
  Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { useDebounce } from 'use-debounce';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import {
  ISignatureRequirement,
  ISignatureRequirementState,
  SigReqJobStatus,
} from 'models/signature-requirement/types';
import { SignatureRequirementClient } from 'clients/signature-requirement-client';
import CustomerDropdown from '../components/customer-dropdown';
import { SectionTitleStyled } from '../components/section-title';
import { SignatureRequirementTable } from './signature-requirement-table';
import { TriggerTypeFilter } from './components/trigger-type-filter';
import { AddSignatureRequirementModal } from './modals/add-signature-requirement-modal';
import { DeleteConfirmationModal } from './modals/delete-confirmation-modal';
import { SignatureRequirementStyles } from './index.styles';
import {
  searchFieldPlaceholder,
  debounceTimeMs,
  triggerTypeDebounceTimeMs,
  initialSearchTerm,
} from './constants';

type ISignatureRequirementProps = WithStyles<typeof SignatureRequirementStyles>;

const SignatureRequirement = (props: ISignatureRequirementProps): JSX.Element => {
  const { classes } = props;

  // State
  const [searchTerm, setSearchTerm] = React.useState<string>(initialSearchTerm);
  const [debouncedSearchTerm] = useDebounce<string | undefined>(searchTerm, debounceTimeMs);
  const [selectedTriggerTypes, setSelectedTriggerTypes] = React.useState<string[]>(['all']);
  // Debounce trigger type changes so rapid multi-select clicks don't spam the API.
  const [debouncedTriggerTypes] = useDebounce<string[]>(
    selectedTriggerTypes,
    triggerTypeDebounceTimeMs,
  );
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState<boolean>(false);
  // Checked rule IDs lifted from table so Exclude button can be enabled/disabled here
  const [checkedRuleIds, setCheckedRuleIds] = React.useState<number[]>([]);
  const hasCheckedItems = checkedRuleIds.length > 0;
  const [reloadDataTrigger, setReloadDataTrigger] = React.useState<Date | undefined>(undefined);
  const [allRules, setAllRules] = React.useState<ISignatureRequirement[]>([]);
  const [rulesetState, setRulesetState] = React.useState<ISignatureRequirementState | null>(null);
  const [isPublishing, setIsPublishing] = React.useState<boolean>(false);

  // Redux state
  const stateSelectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);
  const customers = useTypedSelector(state => state.filters.customers);
  const user = useTypedSelector(state => state.auth.currentUser);
  const [selectedCustomerId, setSelectedCustomerId] =
    React.useState<number>(stateSelectedCustomerId);

  const onLoadData = React.useCallback((data: ISignatureRequirement[]) => {
    setAllRules(data);
  }, []);

  // Permissions
  const userHasPermission = React.useMemo<boolean>(() => {
    return UserUtils.userIsPermitted(
      UserPermissions.SignatureRequirementManage,
      selectedCustomerId,
    );
  }, [selectedCustomerId]);

  // Fetch ruleset state (draft / active job) and poll while a job is running
  const fetchRulesetState = React.useCallback(async () => {
    if (!selectedCustomerId || !userHasPermission) return;
    try {
      const { data } = await SignatureRequirementClient.getRulesetState(selectedCustomerId);
      setRulesetState(data);
    } catch (_) {
      // silently ignore polling errors
    }
  }, [selectedCustomerId, userHasPermission]);

  React.useEffect(() => {
    fetchRulesetState();
  }, [fetchRulesetState, reloadDataTrigger]);

  // Poll every 5 seconds while a job is active
  React.useEffect(() => {
    const isJobActive =
      rulesetState?.activeJob?.status === SigReqJobStatus.Pending ||
      rulesetState?.activeJob?.status === SigReqJobStatus.Processing;

    if (!isJobActive) return () => {};

    const intervalId = setInterval(fetchRulesetState, 5000);
    return () => clearInterval(intervalId);
  }, [rulesetState, fetchRulesetState]);

  const handlePublish = React.useCallback(async () => {
    if (!selectedCustomerId || isPublishing) return;
    setIsPublishing(true);
    try {
      await SignatureRequirementClient.publishRuleset(selectedCustomerId);
      setReloadDataTrigger(new Date());
    } catch (err: any) {
      // 409 = job already active; refresh state to reflect it
      await fetchRulesetState();
    } finally {
      setIsPublishing(false);
    }
  }, [selectedCustomerId, isPublishing, fetchRulesetState]);

  const isJobActive =
    rulesetState?.activeJob?.status === SigReqJobStatus.Pending ||
    rulesetState?.activeJob?.status === SigReqJobStatus.Processing;

  const publishDisabled = isPublishing || isJobActive || !rulesetState?.hasDraft;
  const publishTooltipNotActive = !rulesetState?.hasDraft ? 'No pending changes to publish.' : '';
  const publishTooltip = isJobActive
    ? 'A processing job is currently running. Please wait for it to finish.'
    : publishTooltipNotActive;

  // Clear stale data when permission is lost
  React.useEffect(() => {
    if (!userHasPermission) {
      setAllRules([]);
      setCheckedRuleIds([]);
    }
  }, [userHasPermission]);

  // Effects
  React.useEffect(() => {
    if (selectedCustomerId == null) {
      if (stateSelectedCustomerId != null) {
        setSelectedCustomerId(stateSelectedCustomerId);
      } else if (customers.length > 0) {
        setSelectedCustomerId(customers[0].id);
      }
    }
  }, [stateSelectedCustomerId, customers, selectedCustomerId]);

  const notPublishedText = rulesetState?.hasDraft ? 'Draft — unpublished changes' : 'Published';
  return (
    <>
      <SectionTitleStyled title="Signature Requirement" />

      {/* Ruleset status indicator */}
      {userHasPermission && rulesetState !== null && (
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: rulesetState.hasDraft || isJobActive ? '#ed6c02' : '#2e7d32',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {isJobActive
              ? `Processing (${rulesetState.activeJob?.processed_items} / ${rulesetState.activeJob?.total_items})`
              : notPublishedText}
          </Typography>
          {isJobActive && <CircularProgress size={12} />}
        </Box>
      )}

      <Grid container direction="column" spacing={2}>
        {/* Filters Row */}
        <Grid item>
          <Grid container justifyContent="space-between">
            {/* Left Side - Filters */}
            <Grid item xs="auto">
              <Grid container spacing={1}>
                <Grid item xs="auto">
                  <CustomerDropdown
                    isLabelOn
                    selectedCustomerId={selectedCustomerId}
                    setSelectedCustomerId={customerId => setSelectedCustomerId(customerId)}
                    requiredPermission={UserPermissions.SignatureRequirementManage}
                  />
                </Grid>
                <Grid item xs="auto">
                  <TriggerTypeFilter
                    selectedTriggerTypes={selectedTriggerTypes}
                    setSelectedTriggerTypes={setSelectedTriggerTypes}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right Side - Search Field + Action Buttons */}
            <Grid item xs="auto" className={classes.rightSideContainer}>
              <TextField
                variant="standard"
                placeholder={searchFieldPlaceholder}
                onChange={event => setSearchTerm(event.target.value)}
                value={searchTerm}
                className={classes.searchField}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon className={classes.searchIcon} />
                    </InputAdornment>
                  ),
                  endAdornment:
                    searchTerm != null && searchTerm !== initialSearchTerm ? (
                      <InputAdornment
                        position="end"
                        onClick={() => setSearchTerm(initialSearchTerm)}
                        className={classes.textFieldEndAdornment}
                      >
                        <CloseIcon fontSize="small" />
                      </InputAdornment>
                    ) : null,
                }}
              />
              {userHasPermission ? (
                <>
                  <Button
                    variant="outlined"
                    className={classes.modalButton}
                    onClick={() => setDeleteModalOpen(true)}
                    disabled={!hasCheckedItems}
                  >
                    Exclude
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.modalButton}
                    onClick={() => setAddModalOpen(true)}
                  >
                    Add
                  </Button>
                  <Tooltip title={publishTooltip} disableHoverListener={!publishDisabled}>
                    <span>
                      <Button
                        variant="contained"
                        color="success"
                        className={classes.modalButton}
                        onClick={handlePublish}
                        disabled={publishDisabled}
                        startIcon={
                          isPublishing ? <CircularProgress size={14} color="inherit" /> : null
                        }
                      >
                        Publish
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : null}
            </Grid>
          </Grid>
        </Grid>

        {/* Table */}
        <Grid item>
          {userHasPermission ? (
            <SignatureRequirementTable
              searchTerm={debouncedSearchTerm}
              selectedCustomerId={selectedCustomerId}
              debouncedTriggerTypes={debouncedTriggerTypes}
              userHasPermission={userHasPermission}
              setCheckedRuleIds={setCheckedRuleIds}
              onDataLoaded={onLoadData}
              reloadDataTrigger={reloadDataTrigger}
            />
          ) : (
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              style={{ padding: '48px 16px', color: '#666' }}
            >
              <WarningAmberIcon style={{ fontSize: 48, marginBottom: 8, color: '#ed6c02' }} />
              <Typography variant="subtitle1">
                You do not have permission to manage signature requirements for this customer.
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* Add Modal */}
      <AddSignatureRequirementModal
        open={addModalOpen}
        selectedCustomerId={selectedCustomerId}
        initialValues={{
          name: '',
          site: (() => {
            const customer = customers.find(c => c.id === selectedCustomerId);
            return customer
              ? { label: customer.name, value: selectedCustomerId }
              : { label: '', value: 0 };
          })(),
          trigger: '',
          insurance_bin: '',
          insurance_pcn: '',
          insurance_group: '',
        }}
        onCancel={() => setAddModalOpen(false)}
        onSuccess={() => {
          setReloadDataTrigger(new Date());
          setAddModalOpen(false);
        }}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteModalOpen}
        ruleIdsToDelete={checkedRuleIds}
        allRules={allRules}
        onCancel={() => setDeleteModalOpen(false)}
        onSuccess={() => {
          setReloadDataTrigger(new Date());
          setCheckedRuleIds([]);
          setDeleteModalOpen(false);
        }}
      />
    </>
  );
};

export default withStyles(SignatureRequirementStyles)(SignatureRequirement);
